import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'

import { PageBackButton } from '@/components/common/PageBackButton'
import { ListCard } from '@/components/common/ListCard'
import { defineListFilters } from '@/components/common/filters/core'
import { dateFilter } from '@/components/common/filters/date-filter'
import { DateRangePresets } from '@/components/common/filters/date-range-presets'
import { selectFilter } from '@/components/common/filters/select-filter'
import { textFilter } from '@/components/common/filters/text-filter'
import { useListFilters } from '@/components/common/filters/use-list-filters'
import { ListFilterBar } from '@/components/common/ListFilterBar'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useExpenseReportDaily } from '@/hooks/use-expense-report-query'
import { formatVnd, ymdVN } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { todayYMD } from '@/lib/date-presets'

/**
 * Bộ lọc khai một lần, suy ra: parser URL, mặc định, badge đếm, chip.
 *
 * Hai mốc ngày mặc định là HÔM NAY (báo cáo ngày), nên giá trị đó không ghi vào
 * URL và cũng không tính là đang lọc. Trước đây cả bốn nằm trong `useState` nên
 * không gửi được link kèm kỳ đang xem (R9).
 *
 * Là HÀM chứ không phải hằng module: mặc định phụ thuộc "hôm nay" nên phải tính
 * lại mỗi lần dùng. Nếu dựng sẵn một lần lúc nạp JS thì tab mở qua nửa đêm sẽ
 * kẹt mặc định của ngày hôm trước.
 */
function makeFilters() {
    return defineListFilters({
        from: {
            ...dateFilter({ label: m.expense_filter_from }),
            defaultValue: todayYMD(),
        },
        to: {
            ...dateFilter({ label: m.expense_filter_to }),
            defaultValue: todayYMD(),
        },
        counterparty: textFilter({
            label: m.expense_vendor_name,
            placeholder: m.expense_vendor_name,
        }),
        payment_method: selectFilter<'all' | 'cash' | 'bank_transfer'>({
            label: m.expense_payment_method,
            defaultValue: 'all',
            options: () => [
                { value: 'all', label: m.expense_filter_all_methods() },
                { value: 'cash', label: m.expense_payment_method_cash() },
                {
                    value: 'bank_transfer',
                    label: m.expense_payment_method_bank_transfer(),
                },
            ],
        }),
    })
}

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/reports/daily',
)({
    component: DailyReportPage,
    validateSearch: (search) => makeFilters().validateSearch(search),
})

function DailyReportPage() {
    const navigate = useNavigate({ from: '/accounting/reports/daily' })
    // Dựng lại mỗi lần vào màn để mặc định luôn là hôm nay (xem chú thích ở
    // `makeFilters`); trong một lần mở màn thì giữ nguyên để không nhấp nháy.
    const filtersDef = useMemo(makeFilters, [])
    const { values, barProps } = useListFilters({
        def: filtersDef,
        search: Route.useSearch(),
        onSearchChange: (next, opts) =>
            void navigate({
                search: () => next,
                replace: opts?.replace ?? false,
            }),
        columns: 4,
    })

    // Map bộ lọc → tham số API. Để tường minh ở màn: mỗi màn map một kiểu.
    const { data, isLoading } = useExpenseReportDaily({
        from: values.from,
        to: values.to,
        counterparty: values.counterparty || undefined,
        payment_method:
            values.payment_method === 'all' ? undefined : values.payment_method,
    })

    const entries = data?.entries ?? []
    const summary = data?.summary

    return (
        <div className="space-y-4 page-pad">
            <div className="flex min-w-0 items-center gap-2">
                <PageBackButton className="-my-2 md:-my-1" />
                <h1 className="text-xl font-semibold">
                    {m.expense_report_daily()}
                </h1>
            </div>

            <DateRangePresets
                onPick={(range) =>
                    barProps.onApply({
                        ...values,
                        from: range.from,
                        to: range.to,
                    })
                }
            />
            <ListFilterBar {...barProps} />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 print:hidden">
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">
                            {m.expense_report_total_income()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-emerald-600">
                            {formatVnd(summary?.total_income ?? 0)}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">
                            {m.expense_report_total_expense()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-2xl font-bold text-rose-600">
                            {formatVnd(summary?.total_expense ?? 0)}
                        </p>
                    </CardContent>
                </Card>
                <Card>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm text-muted-foreground">
                            {m.expense_report_net_cash_flow()}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p
                            className={
                                'text-2xl font-bold ' +
                                ((summary?.net_cash_flow ?? 0) >= 0
                                    ? 'text-emerald-600'
                                    : 'text-rose-600')
                            }
                        >
                            {formatVnd(summary?.net_cash_flow ?? 0)}
                        </p>
                    </CardContent>
                </Card>
            </div>

            {/* Mobile: card list */}
            <div className="space-y-2 md:hidden">
                {!isLoading && entries.length === 0 ? (
                    <div className="py-8 text-center text-muted-foreground">
                        {m.expense_report_no_data()}
                    </div>
                ) : (
                    entries.map((entry) => (
                        <ListCard
                            key={entry.code}
                            title={
                                <span className="font-mono text-sm font-semibold">
                                    {entry.code}
                                </span>
                            }
                            subtitle={entry.counterparty}
                            metric={{
                                value: `${entry.kind === 'income' ? '+' : '−'}${formatVnd(entry.amount)}`,
                                tone:
                                    entry.kind === 'income'
                                        ? 'positive'
                                        : 'negative',
                            }}
                            badge={
                                <Badge
                                    variant={
                                        entry.kind === 'income'
                                            ? 'default'
                                            : 'destructive'
                                    }
                                >
                                    {entry.kind === 'income'
                                        ? m.expense_kind_income()
                                        : m.expense_kind_expense()}
                                </Badge>
                            }
                            fields={[
                                {
                                    label: m.expense_payment_method(),
                                    value: entry.payment_method,
                                },
                                {
                                    label: m.expense_payment_status(),
                                    value: entry.payment_status,
                                },
                                {
                                    label: m.expense_date(),
                                    value: ymdVN(entry.occurred_at),
                                    full: true,
                                },
                            ]}
                        />
                    ))
                )}
            </div>

            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
                <Table>
                    <TableHeader>
                        <TableRow>
                            <TableHead>{m.expense_daily_col_code()}</TableHead>
                            <TableHead>{m.expense_daily_col_type()}</TableHead>
                            <TableHead>{m.expense_vendor_name()}</TableHead>
                            <TableHead className="text-right">
                                {m.expense_amount()}
                            </TableHead>
                            <TableHead>{m.expense_payment_method()}</TableHead>
                            <TableHead>{m.expense_payment_status()}</TableHead>
                            <TableHead>{m.expense_date()}</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {!isLoading && entries.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={7}
                                    className="text-center text-muted-foreground py-8"
                                >
                                    {m.expense_report_no_data()}
                                </TableCell>
                            </TableRow>
                        )}
                        {entries.map((entry) => (
                            <TableRow key={entry.code}>
                                <TableCell className="font-mono text-xs">
                                    {entry.code}
                                </TableCell>
                                <TableCell>
                                    <Badge
                                        variant={
                                            entry.kind === 'income'
                                                ? 'default'
                                                : 'destructive'
                                        }
                                    >
                                        {entry.kind === 'income'
                                            ? m.expense_kind_income()
                                            : m.expense_kind_expense()}
                                    </Badge>
                                </TableCell>
                                <TableCell>{entry.counterparty}</TableCell>
                                <TableCell
                                    className={
                                        'text-right font-mono ' +
                                        (entry.kind === 'income'
                                            ? 'text-emerald-600'
                                            : 'text-rose-600')
                                    }
                                >
                                    {entry.kind === 'income' ? '+' : '−'}
                                    {formatVnd(entry.amount)}
                                </TableCell>
                                <TableCell>{entry.payment_method}</TableCell>
                                <TableCell>{entry.payment_status}</TableCell>
                                <TableCell>
                                    {ymdVN(entry.occurred_at)}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </div>
        </div>
    )
}
